/**
 * Dialog — Radix-Dialog-Hülle der Marke: Backdrop bg-ink/40 mit Unschärfe, Content bg-card mit
 * Rand und Schatten, Titel in der Serifenschrift. Den Fokus gibt sie beim Schließen dem zurück,
 * der ihn beim Öffnen hatte (`useReturnFocus`).
 *
 * Höhe: Der Content ist eine Spalte mit Höhendeckel. `DialogBody` ist der einzige
 * scrollende Teil, Kopf und Fuß bleiben stehen — sonst wandert die primäre Aktion
 * aus dem Fenster und Radix' Body-Scroll-Lock lässt keinen Ausweg. Der Deckel nutzt
 * `dvh`, weil `vh` auf Mobile die eingeblendete Browserleiste ignoriert.
 *
 * Dialoge ohne `DialogBody` fallen auf Scrollen des ganzen Contents zurück. Das ist
 * bewusst so: Kopf und Fuß scrollen dann mit, aber nichts wird je abgeschnitten.
 */
import * as RadixDialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib/cn';
import { useReturnFocus } from './useReturnFocus';

export const Dialog = RadixDialog.Root;
export const DialogTrigger = RadixDialog.Trigger;
export const DialogClose = RadixDialog.Close;

export const DialogContent = forwardRef<
  HTMLDivElement,
  RadixDialog.DialogContentProps & { showClose?: boolean }
>(({ className, children, showClose = true, onOpenAutoFocus, onCloseAutoFocus, ...props }, ref) => {
  const { t } = useTranslation();
  const returnFocus = useReturnFocus(onOpenAutoFocus, onCloseAutoFocus);
  return (
    <RadixDialog.Portal>
      <RadixDialog.Overlay className="bg-ink/40 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 backdrop-blur-sm" />
      <RadixDialog.Content
        ref={ref}
        className={cn(
          'bg-card border-line/50 shadow-card text-ink fixed left-1/2 top-1/2 z-50 w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-lg border',
          'flex max-h-[calc(100dvh-2rem)] flex-col overflow-y-auto overscroll-contain',
          'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          className,
        )}
        {...props}
        {...returnFocus}
      >
        {children}
        {showClose && (
          <RadixDialog.Close
            className="text-ink-soft hover:text-ink absolute right-3 top-2 grid min-h-11 min-w-11 place-items-center rounded-md transition-colors"
            aria-label={t('common.close')}
          >
            <X size={18} strokeWidth={2} />
          </RadixDialog.Close>
        )}
      </RadixDialog.Content>
    </RadixDialog.Portal>
  );
});
DialogContent.displayName = 'DialogContent';

export function DialogHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('border-line/50 shrink-0 border-b px-6 py-4', className)} {...props} />;
}

export function DialogTitle({ className, ...props }: RadixDialog.DialogTitleProps) {
  return (
    <RadixDialog.Title
      className={cn('text-ink font-serif text-xl font-medium tracking-tight', className)}
      {...props}
    />
  );
}

export function DialogDescription({ className, ...props }: RadixDialog.DialogDescriptionProps) {
  return (
    <RadixDialog.Description className={cn('text-ink-soft mt-1 text-sm', className)} {...props} />
  );
}

export function DialogBody({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('min-h-0 flex-1 overflow-y-auto overscroll-contain p-6', className)}
      {...props}
    />
  );
}

export function DialogFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'bg-card-alt/50 border-line/50 flex shrink-0 items-center justify-end gap-2 rounded-b-lg border-t px-6 py-4',
        // env() ist auf Desktop 0 — der Wert bleibt dort exakt py-4 und wächst nur dort,
        // wo eine Systemleiste den Fuß sonst verdecken würde.
        'pb-[calc(1rem+env(safe-area-inset-bottom))]',
        className,
      )}
      {...props}
    />
  );
}

/**
 * ConfirmDialog — spezialisiert für irreversible Aktionen.
 * Destructive-Button rechts, Ghost-Cancel links.
 */
export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'destructive' | 'brand';
  loading?: boolean;
  footerClassName?: string;
  buttonClassName?: string;
  onConfirm: () => void | Promise<void>;
}

import { Button } from './Button';

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Bestätigen',
  cancelLabel = 'Abbrechen',
  variant = 'destructive',
  loading,
  footerClassName,
  buttonClassName,
  onConfirm,
}: ConfirmDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <DialogFooter className={footerClassName}>
          <Button
            variant="ghost"
            className={buttonClassName}
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            {cancelLabel}
          </Button>
          <Button
            variant={variant}
            className={buttonClassName}
            onClick={onConfirm}
            loading={loading}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
